import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, ThumbDown as ThumbDownIcon } from '@mui/icons-material';
import axios from 'axios';
import { formatCacheEntryPreview } from '../utils/cacheUsageUtils';

export default function CacheUsageDialog({ open, onClose, cacheUsage, origin = 'client' }) {
  const entries = useMemo(
    () => (Array.isArray(cacheUsage?.entries) ? cacheUsage.entries : []),
    [cacheUsage]
  );
  const [loading, setLoading] = useState(false);
  const [existenceByIndex, setExistenceByIndex] = useState({});
  const [invalidatingIndex, setInvalidatingIndex] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmEntry, setConfirmEntry] = useState(null);

  const getAuthHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('token');
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  };

  const loadExistence = useCallback(async () => {
    if (!entries.length) {
      setExistenceByIndex({});
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post('/api/cache/entry-status', { entries }, { headers: getAuthHeaders() });
      const results = Array.isArray(response.data?.results) ? response.data.results : [];
      const next = {};
      results.forEach(({ index, exists }) => {
        next[index] = exists === true;
      });
      setExistenceByIndex(next);
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Failed to check cache entry status.');
    } finally {
      setLoading(false);
    }
  }, [entries]);

  useEffect(() => {
    if (!open) return;
    setErrorMessage('');
    loadExistence();
  }, [open, loadExistence]);

  const handleInvalidate = async (entry, index) => {
    setInvalidatingIndex(index);
    setErrorMessage('');
    try {
      await axios.post(
        '/api/cache/invalidate-entry',
        { store: entry.store, key: entry.key, origin },
        { headers: getAuthHeaders() }
      );
      setExistenceByIndex((prev) => ({ ...prev, [index]: false }));
    } catch (error) {
      const status = error.response?.status;
      if (status === 404) {
        setExistenceByIndex((prev) => ({ ...prev, [index]: false }));
      } else {
        setErrorMessage(error.response?.data?.error || 'Failed to invalidate cache entry.');
      }
    } finally {
      setInvalidatingIndex(null);
      setConfirmEntry(null);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="cache-usage-dialog-title">
        <DialogTitle id="cache-usage-dialog-title" sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
          <Typography variant="h6" component="span" sx={{ flex: 1 }}>
            Cache usage
          </Typography>
          <IconButton aria-label="Close cache usage dialog" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!loading && entries.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No cache usage recorded for this message.
            </Typography>
          )}
          {!loading && entries.length > 0 && (
            <List dense disablePadding>
              {entries.map((entry, index) => {
                const preview = formatCacheEntryPreview(entry);
                const exists = existenceByIndex[index] === true;
                const showInvalidate = exists;
                return (
                  <ListItem
                    key={`${entry.store}-${index}`}
                    secondaryAction={
                      showInvalidate ? (
                        <Tooltip title="Invalidate this cache entry" arrow>
                          <span>
                            <IconButton
                              edge="end"
                              aria-label="Invalidate cache entry"
                              disabled={invalidatingIndex === index}
                              onClick={() => setConfirmEntry({ entry, index })}
                            >
                              {invalidatingIndex === index ? (
                                <CircularProgress size={18} />
                              ) : (
                                <ThumbDownIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null
                    }
                    sx={{
                      alignItems: 'flex-start',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1.25,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pr: 4 }}>
                          <Typography variant="subtitle2">{entry.label || entry.store}</Typography>
                          {entry.cacheName && (
                            <Chip size="small" label={entry.cacheName} variant="outlined" />
                          )}
                        </Box>
                      }
                      secondary={
                        preview ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                            {preview}
                          </Typography>
                        ) : null
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
          {errorMessage && (
            <Typography variant="body2" color="error.main" sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmEntry)} onClose={() => setConfirmEntry(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Invalidate cache entry?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This removes the cached value. Future requests may take longer until the cache is populated again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEntry(null)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              if (confirmEntry) {
                handleInvalidate(confirmEntry.entry, confirmEntry.index);
              }
            }}
          >
            Invalidate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
